import React, { useEffect, useRef, useState } from "react";
import TextField from '@mui/material/TextField';
import { observer } from "mobx-react";
import Cart from "services/store/Cart";
import ProductStore from "services/store/ProductStore";
import { useNavigate } from "react-router-dom";
import "./RightPanel.scss"
import CartItem from "components/CartItem/CartItem";
import Button from '@mui/material/Button';
import { toast } from 'react-toastify';

const INACTIVITY_TOAST_ID = "right-panel-inactivity";
const CART_MESSAGE_TOAST_ID = "right-panel-cart-message";

const RightPanel = ({token, opened}) => {
  const navigate = useNavigate();
  const [canOrder, setCanOrder] = useState(false);
  const [comment, setComment] = useState("");
  const inactivityTimerRef = useRef(null);
  const hasShownInactivityToastRef = useRef(false);
  const lastCartMessageRef = useRef("");
  
  const redirectToPayment = () => {
    if (!canOrder) return;

    navigate("/payment");
  }

  const setCartComment = (value) => {
    setComment(value);
    Cart.comment = value;
  }

  const handleBeforeRemove = async () => {
    ProductStore.isUpdatingCart = true;
  };

  const handleAfterRemove = async () => {
    try {
      await ProductStore.refreshSearch(token, { silent: true });
    } finally {
      ProductStore.isUpdatingCart = false;
    }
  };

  const clearInactivityTimer = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  };

  const scheduleInactivityToast = () => {
    clearInactivityTimer();

    if (!opened || hasShownInactivityToastRef.current) {
      return;
    }

    inactivityTimerRef.current = setTimeout(() => {
      toast.info("Cliquez à nouveau sur le « logo panier » pour continuer vos achats", {
        position: "bottom-right",
        autoClose: 6000,
        toastId: INACTIVITY_TOAST_ID,
      });
      hasShownInactivityToastRef.current = true;
      inactivityTimerRef.current = null;
    }, 10000);
  };

  useEffect(() => {
    if (Cart.cartMessage && opened && lastCartMessageRef.current !== Cart.cartMessage) {
      lastCartMessageRef.current = Cart.cartMessage;
      toast.info(Cart.cartMessage, {
        position: "top-right",
        autoClose: 4000,
        toastId: CART_MESSAGE_TOAST_ID,
      });
    }
  }, [Cart.cartMessage, opened]);

  useEffect(() => {
    if (Cart.cart?.length > 0) {
      setCanOrder(true);
    } else {
      setCanOrder(false);
    }
  }, [Cart.cart?.length, token]);

  useEffect(() => {
    if (!opened) {
      clearInactivityTimer();
      hasShownInactivityToastRef.current = false;
      toast.dismiss(INACTIVITY_TOAST_ID);
      return undefined;
    }

    hasShownInactivityToastRef.current = false;
    scheduleInactivityToast();

    const resetTimer = () => {
      scheduleInactivityToast();
    };

    document.addEventListener('mousedown', resetTimer);
    document.addEventListener('keydown', resetTimer);
    document.addEventListener('touchstart', resetTimer);
    document.addEventListener('scroll', resetTimer);

    return () => {
      clearInactivityTimer();
      document.removeEventListener('mousedown', resetTimer);
      document.removeEventListener('keydown', resetTimer);
      document.removeEventListener('touchstart', resetTimer);
      document.removeEventListener('scroll', resetTimer);
    };
  }, [opened]);

  return (
    <div className={`right-panel ${opened ? "opened" : ""}`} id="menu-right">
      <p>Panier</p>
      <div className="cart-container">
        {Cart.cart.map((product, index) => (
          <CartItem
            product={product}
            token={token}
            key={index}
            onRemove={handleBeforeRemove}
            afterRemove={handleAfterRemove}
          />
        ))}
      </div>
      <p>Total commandé : {Cart.orderPrice} €</p>
      <p>Colis estimés : {Cart.parcelCount}</p>

      <Button 
        disabled={!Cart.canValidate || !canOrder} 
        style={{fontSize: '16px'}} 
        className="w-100 mt-3 mb-5" 
        variant="contained" 
        type="button" 
        onClick={redirectToPayment}>
          Commander
      </Button>

      {Cart.paymentCard && (
        <div>
          <TextField 
              id="comment" 
              label="Commentaire" 
              value={comment}
              variant="outlined"
              size="small"
              multiline
              rows={3}
              onChange={e => setCartComment(e.target.value)} />
        </div>
      )}
    </div>
  )
}

export default observer(RightPanel);
